import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ShieldCheck, Building2, User, Save, KeyRound, Loader2, Upload, Trash2, ImageIcon, FileSignature } from "lucide-react";
import { toast } from "sonner";
import VerifactuSettings from "../components/VerifactuSettings";

export default function Settings() {
  const { t, lang } = useI18n();
  const { user, activeCompany, refresh } = useAuth();
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (activeCompany) setCompany(activeCompany);
  }, [activeCompany]);

  if (!company) return <div className="text-slate-400">{t("common.loading")}</div>;

  const onLogoPick = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error(lang === "es" ? "Máximo 1 MB" : "Màxim 1 MB");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/companies/${company.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCompany({ ...company, logo_url: r.data.logo_url });
      toast.success(lang === "es" ? "Logo subido" : "Logo pujat");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const onLogoDelete = async () => {
    if (!window.confirm(lang === "es" ? "¿Eliminar el logo?" : "Eliminar el logo?")) return;
    try {
      await api.delete(`/companies/${company.id}/logo`);
      setCompany({ ...company, logo_url: "" });
      toast.success(lang === "es" ? "Logo eliminado" : "Logo eliminat");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: company.name,
        nif_cif: company.nif_cif || "",
        address: company.address || "",
        city: company.city || "",
        postal_code: company.postal_code || "",
        country: company.country || "",
        phone: company.phone || "",
        email: company.email || "",
        iban: company.iban || "",
        bank_name: company.bank_name || "",
        bic_swift: company.bic_swift || "",
        website: company.website || "",
        conditions: company.conditions || "",
        logo_url: company.logo_url || "",
        default_iva: Number(company.default_iva || 21),
        default_irpf: Number(company.default_irpf || 0),
        default_vat_mode: company.default_vat_mode === "included" ? "included" : "excluded",
        invoice_prefix: company.invoice_prefix || "F",
        mercantile_registry: company.mercantile_registry || "",
        quote_prefix: company.quote_prefix || "P",
      };
      await api.put(`/companies/${company.id}`, payload);
      toast.success(t("settings.saved"));
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">
        {t("settings.title")}
      </h1>

      <Tabs defaultValue="company" className="w-full">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="company" data-testid="tab-company"><Building2 size={14} className="mr-2" /> {t("settings.company")}</TabsTrigger>
          <TabsTrigger value="verifactu" data-testid="tab-verifactu"><FileSignature size={14} className="mr-2" /> VeriFactu</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account"><User size={14} className="mr-2" /> {t("settings.account")}</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security"><ShieldCheck size={14} className="mr-2" /> {t("settings.security")}</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <div className="card-surface p-6 space-y-6 max-w-2xl">
            {/* Logo upload section */}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                {t("settings.logo", "Logotip")}
              </label>
              <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                <div
                  className="w-28 h-28 shrink-0 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center overflow-hidden"
                  data-testid="logo-preview"
                >
                  {company.logo_url ? (
                    <img
                      src={company.logo_url}
                      alt="logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-slate-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0 w-full">
                  <p className="text-sm text-slate-300 mb-1">
                    {lang === "es"
                      ? "Aparecerá en tus facturas y presupuestos"
                      : "Sortirà a les teves factures i pressupostos"}
                  </p>
                  <p className="text-xs text-slate-500 mb-3">
                    PNG, JPG, WEBP, SVG · {lang === "es" ? "máx." : "màx."} 1 MB
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => onLogoPick(e.target.files?.[0])}
                      data-testid="logo-file-input"
                    />
                    <Button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="logo-upload-btn"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 size={14} className="mr-2 animate-spin" />
                          {lang === "es" ? "Subiendo..." : "Pujant..."}
                        </>
                      ) : (
                        <>
                          <Upload size={14} className="mr-2" />
                          {company.logo_url
                            ? (lang === "es" ? "Cambiar" : "Canviar")
                            : (lang === "es" ? "Subir logo" : "Pujar logo")}
                        </>
                      )}
                    </Button>
                    {company.logo_url && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onLogoDelete}
                        data-testid="logo-delete-btn"
                        className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                      >
                        <Trash2 size={14} className="mr-2" />
                        {lang === "es" ? "Eliminar" : "Eliminar"}
                      </Button>
                    )}
                  </div>

                  {/* Logo size selector — only when there is a logo */}
                  {company.logo_url && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                        {lang === "es" ? "Tamaño en el PDF" : "Mida al PDF"}
                      </label>
                      <div className="flex flex-wrap gap-1.5" data-testid="logo-size-group">
                        {[
                          { v: "small", l: lang === "es" ? "Pequeño" : "Petit" },
                          { v: "medium", l: lang === "es" ? "Mediano" : "Mitjà" },
                          { v: "large", l: lang === "es" ? "Grande" : "Gran" },
                        ].map((opt) => {
                          const active = (company.logo_size || "medium") === opt.v;
                          return (
                            <button
                              key={opt.v}
                              type="button"
                              onClick={() => setCompany({ ...company, logo_size: opt.v })}
                              data-testid={`logo-size-${opt.v}`}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                                active
                                  ? "bg-blue-600 border-blue-500 text-white"
                                  : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
                              }`}
                            >
                              {opt.l}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-2">
                        {lang === "es"
                          ? "Recuerda pulsar Guardar para aplicar el cambio."
                          : "Recorda prémer Desa per aplicar el canvi."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ["name", "Nom"], ["nif_cif", "NIF/CIF"],
                ["email", "Email"], ["phone", "Telèfon"],
                ["website", "Web"], ["address", "Adreça"],
                ["city", "Població"], ["postal_code", "C. Postal"],
                ["country", "País"],
                ["bank_name", "Banc"],
                ["iban", "IBAN"], ["bic_swift", "BIC/SWIFT"],
                ["default_iva", t("settings.default_iva")], ["default_irpf", t("settings.default_irpf")],
                ["quote_prefix", t("settings.quote_prefix")], ["invoice_prefix", t("settings.invoice_prefix")],
              ].map(([k, l]) => (
                <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                  <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">{l}</label>
                  <Input
                    data-testid={`company-${k}`}
                    value={company[k] ?? ""}
                    onChange={(e) => setCompany({ ...company, [k]: e.target.value })}
                    className="bg-[#05070D] border-white/10"
                  />
                  {k === "email" && (
                    <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                      {lang === "es"
                        ? "Cuando un cliente reciba una factura por email y haga clic en \"Responder\", su respuesta llegará a esta dirección."
                        : "Quan un client rebi una factura per email i cliqui \"Respondre\", la seva resposta arribarà a aquesta adreça."}
                    </p>
                  )}
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">
                  Condicions del PDF (una per línia)
                </label>
                <Textarea
                  data-testid="company-conditions"
                  value={company.conditions ?? ""}
                  onChange={(e) => setCompany({ ...company, conditions: e.target.value })}
                  className="bg-[#05070D] border-white/10 min-h-[90px]"
                  placeholder="Factura emesa i dipositada en comptabilitat.&#10;Operació exempta de retenció.&#10;Venciment: al comptat."
                />
              </div>
              {/* Registro Mercantil — legal requirement for Spanish SL/SA/SLU
                  corporations (Real Decreto Legislativo 1/2010, art. 24).
                  Autònoms can leave it empty. Printed in small grey text at
                  the bottom of every invoice/quote when filled in. */}
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">
                  {lang === "es" ? "Registro Mercantil (solo sociedades)" : "Registre Mercantil (només societats)"}
                </label>
                <Textarea
                  data-testid="company-mercantile-registry"
                  value={company.mercantile_registry ?? ""}
                  onChange={(e) => setCompany({ ...company, mercantile_registry: e.target.value })}
                  className="bg-[#05070D] border-white/10 min-h-[64px]"
                  placeholder={lang === "es"
                    ? "Ej: Inscrita en el Registro Mercantil de Tarragona, Tomo 449, Folio 156, Hoja T-9876"
                    : "Ex: Inscrita al Registre Mercantil de Tarragona, Tom 449, Foli 156, Full T-9876"}
                />
                <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                  {lang === "es"
                    ? "Obligatorio para SL/SA/SLU (art. 24 RDLeg 1/2010). Se imprime en tamaño pequeño al pie de facturas y presupuestos. Los autónomos pueden dejarlo vacío."
                    : "Obligatori per a SL/SA/SLU (art. 24 RDLeg 1/2010). S'imprimeix en tipografia petita al peu de factures i pressupostos. Els autònoms poden deixar-ho buit."}
                </p>
              </div>
              {/* Default VAT mode for the voice agent / editor. "included"
                  is typical for retail/horeca where displayed prices are
                  always gross; "excluded" is the B2B / services default. */}
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">
                  {t("settings.vat_mode_label")}
                </label>
                <div
                  className="inline-flex rounded-xl bg-[#05070D] border border-white/10 p-1"
                  data-testid="company-default-vat-mode"
                >
                  {[
                    { key: "excluded", label: t("settings.vat_mode_excluded") },
                    { key: "included", label: t("settings.vat_mode_included") },
                  ].map((opt) => {
                    const active = (company.default_vat_mode || "excluded") === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        data-testid={`vat-mode-${opt.key}`}
                        onClick={() => setCompany({ ...company, default_vat_mode: opt.key })}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                          active
                            ? "bg-blue-500 text-white shadow"
                            : "text-slate-300 hover:bg-white/5"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  {t("settings.vat_mode_hint")}
                </p>
              </div>
            </div>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="company-save-btn"
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              {t("clients.save")}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="verifactu">
          <div className="card-surface p-6 max-w-3xl">
            {activeCompany?.id ? (
              <VerifactuSettings companyId={activeCompany.id} />
            ) : (
              <div className="text-sm text-slate-400 text-center py-8">
                {t("settings.no_company") || "—"}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="account">
          <div className="card-surface p-6 space-y-3 max-w-lg">
            <div className="text-sm text-slate-400">{t("auth.email")}</div>
            <div className="font-medium">{user.email}</div>
            <div className="text-sm text-slate-400 mt-3">{t("auth.full_name")}</div>
            <div className="font-medium">{user.full_name}</div>
            <div className="text-sm text-slate-400 mt-3">Idioma</div>
            <div className="font-medium uppercase">{user.language}</div>
          </div>
        </TabsContent>

        <TabsContent value="security">
          <TwoFAPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TwoFAPanel() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetup(r.data);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA activat!");
      setSetup(null);
      setCode("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Codi invàlid");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!code) {
      toast.error("Introdueix el codi 2FA per desactivar");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("2FA desactivat");
      setCode("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-6 max-w-lg space-y-4" data-testid="twofa-panel">
      <div className="flex items-center gap-3">
        <ShieldCheck size={20} className={user.totp_enabled ? "text-emerald-400" : "text-slate-400"} />
        <div>
          <h3 className="font-semibold">Autenticació de dos factors</h3>
          <p className="text-xs text-slate-400">
            {user.totp_enabled
              ? "El teu compte està protegit amb 2FA"
              : "Afegeix una capa extra de seguretat amb una app TOTP"}
          </p>
        </div>
      </div>

      {!user.totp_enabled && !setup && (
        <Button onClick={startSetup} disabled={busy} data-testid="twofa-setup-btn" className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400">
          {t("settings.twofa_enable")}
        </Button>
      )}

      {setup && (
        <div className="space-y-4 pt-2">
          <p className="text-sm text-slate-400">{t("settings.twofa_scan")}</p>
          <img src={setup.qr_data_url} alt="QR" className="w-40 h-40 bg-white p-2 rounded-lg" data-testid="twofa-qr" />
          <div className="text-xs text-slate-500 font-mono break-all">
            Secret: <span className="text-slate-300">{setup.secret}</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              placeholder="000000"
              className="bg-[#05070D] border-white/10 tracking-widest text-center font-mono"
              data-testid="twofa-code-input"
            />
            <Button onClick={verify} disabled={busy || code.length !== 6} data-testid="twofa-verify-btn" className="bg-emerald-600 hover:bg-emerald-500">
              <KeyRound size={14} className="mr-2" />
              {t("settings.twofa_verify")}
            </Button>
          </div>
        </div>
      )}

      {user.totp_enabled && (
        <div className="space-y-3 pt-2 border-t border-white/5">
          <p className="text-xs text-slate-400">Per desactivar, introdueix el codi actual:</p>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              placeholder="000000"
              className="bg-[#05070D] border-white/10 tracking-widest text-center font-mono"
              data-testid="twofa-disable-code"
            />
            <Button onClick={disable} disabled={busy} variant="outline" className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid="twofa-disable-btn">
              {t("settings.twofa_disable")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
